import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AddClientResponse,
  CheckBinanceAccountStatus,
  CreateAccessTokenResponse,
  OpenMessage,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import {
  BINANCE_API_MANAGEMENT_LINK,
  BYBIT_API_MANAGEMENT_LINK,
  KUCOIN_API_MANAGEMENT_LINK,
  OKX_API_MANAGEMENT_LINK,
  PRIMARY_EXCHANGES,
} from "@shared/common/constants";
import useDelayedLoading from "@shared/features/onboarding/hooks/useDelayedLoading";
import {
  CREATE_ACCOUNT_ERROR_DEFAULT_TITLE,
  QUICK_CONNECT_API_EXISTS_ERROR_CODE,
} from "@shared/features/onboarding/constants";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import useUrlQuickConnect from "@shared/features/onboarding/hooks/useUrlQuickConnect";
import {
  commonActions,
  selectShouldFetchPortfolioData,
} from "@shared/slices/common.slice";

interface useQuickConnectProviderProps {
  queryCode?: string | null;
  queryState?: string | null;
  queryData: {
    statusCode: string | null;
    failMessage: string | null;
    accountBaseName: string | null;
    exchangesList?: string | null;
  };
  onInitializeCodeAndState?: () => void;
  openMessage: OpenMessage;
  isOnboarding?: boolean;
  onOpenModal?: () => void;
  onCloseModal?: () => void;
  onSubmitSuccess?: () => void;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
  getErrorNotification?: (description: string, title?: string) => void;
  onCreateAccessToken?: (primaryExchange: string) => void;
  pollingAccountCreated?: boolean;
  isMobileApp?: boolean;
}

const useQuickConnectProvider = ({
  queryCode,
  queryState,
  queryData,
  onInitializeCodeAndState,
  openMessage,
  isOnboarding,
  onOpenModal,
  onCloseModal,
  onSubmitSuccess,
  getSuccessMessage,
  getErrorNotification,
  onCreateAccessToken: propsOnCreateAccessToken,
  pollingAccountCreated,
  isMobileApp,
}: useQuickConnectProviderProps) => {
  const dispatch = useDispatch();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const shouldFetchPortfolioData = useSelector(selectShouldFetchPortfolioData);
  const [hasAccessToken, setHasAccessToken] = useState(false);
  const [hasMatchAccount, setHasMatchAccount] = useState<boolean>();
  const [createApiKeysFailed, setCreateApiKeysFailed] = useState<number>();
  const [code, setCode] = useState<string | null>();
  const [state, setState] = useState<string | null>();
  const [exchangeToClientName, setExchangeToClientName] =
    useState<Record<string, string>>();
  const [createApiKeysErrorMessage, setCreateApiKeysErrorMessage] =
    useState<string>();
  const [isInitialized, setIsInitialized] = useState(!queryState || !queryCode);
  const [quickConnectSucceeded, setQuickConnectSucceeded] = useState(false);

  const {
    createAccountDrawerOpen,
    openCreateAccountDrawer,
    closeCreateAccountDrawer,
    resetFormValues,
    selectedSubExchanges,
    setSelectedSubExchanges,
    setAccountName,
    getSubExchanges,
    exchangesData,
  } = useContext(AccountFormContext)!;

  const closeDrawer = () => {
    if (!isOnboarding) {
      closeCreateAccountDrawer();
      resetQuickConnectState();
      resetFormValues();
      onCloseModal?.();
    }
  };

  useUrlQuickConnect({
    queryData,
    openMessage,
    onSubmitSuccess,
    closeDrawer,
    getSuccessMessage,
    getErrorNotification,
    isOnboarding,
    isMobileApp,
  });

  const [getPortfolioData] =
    sharedLib.sharedApi.portfolio.useLazyGetPortfolioDataQuery();

  const [
    createAccessToken,
    { data: createAccessTokenResponse, isLoading: createAccessTokenLoading },
  ] = sharedLib.sharedApi.configuration.useCreateAccessTokenMutation();

  const primaryExchange = createAccessTokenResponse?.nested_exchange;

  const [
    checkAccountStatus,
    {
      data: binanceAccountStatus,
      isLoading: checkAccountStatusLoading,
      isFetching: checkAccountStatusFetching,
    },
  ] = sharedLib.sharedApi.configuration.useLazyCheckAccountStatusQuery();

  const [
    createApiKeys,
    { isLoading: createApiKeysLoading, isSuccess: createApiKeysSuccess },
  ] = sharedLib.sharedApi.configuration.useCreateApiKeysMutation();

  const loading =
    createAccessTokenLoading ||
    checkAccountStatusLoading ||
    createApiKeysLoading;
  const isLoading = useDelayedLoading(loading, 100);
  const quickConnectLoading = isLoading && !createApiKeysFailed;

  const onCreateAccessToken = (response: CreateAccessTokenResponse) => {
    if (!state) return;
    const {
      nested_exchange: primaryExchange,
      exchange_to_client_name: exchangeToClientNameResponse,
      client_base_name: accountName,
    } = response;
    propsOnCreateAccessToken?.(primaryExchange);
    setExchangeToClientName(exchangeToClientNameResponse);
    setAccountName(accountName);

    if (primaryExchange === PRIMARY_EXCHANGES.BINANCE) {
      checkAccountStatus({ state });
    } else if (
      [
        PRIMARY_EXCHANGES.OKX,
        PRIMARY_EXCHANGES.BYBIT,
        PRIMARY_EXCHANGES.KUCOIN,
        PRIMARY_EXCHANGES.BINGX,
      ].includes(primaryExchange)
    ) {
      setHasMatchAccount(true);
    }
    setHasAccessToken(true);
  };

  const createAccessTokenHandler = () => {
    if (!code || !state) return;
    createAccessToken({ code, state })
      .unwrap()
      .then((response) => onCreateAccessToken(response))
      .catch((error) => {
        openMessage(error?.data?.message, "error");
        handleFetchPortfolioData();
        setIsInitialized(true);
      });
  };

  const binanceSelectedSubExchangesValid = useCallback(() => {
    return !selectedSubExchanges?.some(
      (subExchange) =>
        binanceAccountStatus?.[
          subExchange.exchange as keyof CheckBinanceAccountStatus
        ] === false
    );
  }, [selectedSubExchanges, binanceAccountStatus]);

  const handleFetchPortfolioData = useCallback(() => {
    if (shouldFetchPortfolioData) {
      try {
        getPortfolioData(undefined);
      } catch {}
    } else {
      dispatch(commonActions.setShouldFetchPortfolioData(true));
    }
  }, [shouldFetchPortfolioData]);

  useEffect(() => {
    if (!code && !state && queryCode && queryState) {
      setCode(queryCode);
      setState(queryState);
      onInitializeCodeAndState?.();
    }
  }, [queryCode, queryState]);

  useEffect(() => {
    createAccessTokenHandler();
  }, [code, state]);

  useEffect(() => {
    if (exchangeToClientName && exchangesData) {
      setSelectedSubExchanges(getSubExchanges(exchangeToClientName));
    }
  }, [exchangeToClientName, exchangesData]);

  useEffect(() => {
    if (binanceAccountStatus) {
      setHasMatchAccount(binanceSelectedSubExchangesValid());
    }
  }, [binanceAccountStatus]);

  useEffect(() => {
    if (hasMatchAccount) {
      createApiKeysHandler();
    } else if (hasMatchAccount === false) {
      handleFetchPortfolioData();
    }
  }, [hasMatchAccount]);

  useEffect(() => {
    if (
      code &&
      state &&
      binanceAccountStatus &&
      hasMatchAccount === false &&
      !createAccountDrawerOpen
    ) {
      openDrawer();
    }
  }, [
    binanceAccountStatus,
    hasMatchAccount,
    code,
    state,
    createAccountDrawerOpen,
  ]);

  useEffect(() => {
    if (
      (createApiKeysSuccess || pollingAccountCreated) &&
      user?.has_clients_connected
    )
      onSubmitSuccess?.();
  }, [
    createApiKeysSuccess,
    user?.has_clients_connected,
    pollingAccountCreated,
  ]);

  const resetCodeAndState = () => {
    setCode(undefined);
    setState(undefined);
  };

  const resetQuickConnectState = () => {
    resetCodeAndState();
    setHasAccessToken(false);
    setCreateApiKeysFailed(undefined);
    setHasMatchAccount(undefined);
  };

  const openDrawer = () => {
    if (!isOnboarding) {
      openCreateAccountDrawer();
      onOpenModal?.();
    }
  };

  const createApiKeysHandler = useCallback(() => {
    if (!state || !exchangeToClientName) return;
    createApiKeys({ state, exchange_to_client_name: exchangeToClientName })
      .unwrap()
      .then((response: any) => {
        closeDrawer();
        getSuccessMessage?.(response);
        if (isOnboarding) {
          setQuickConnectSucceeded(true);
        }
      })
      .catch((error) => {
        setCreateApiKeysFailed(error.status);
        if (error.status === QUICK_CONNECT_API_EXISTS_ERROR_CODE) {
          setCreateApiKeysErrorMessage(error.data.message);
        } else {
          resetCodeAndState();
          if (error.status === 400) {
            getErrorNotification?.(
              error?.data?.message,
              error.data.title ?? CREATE_ACCOUNT_ERROR_DEFAULT_TITLE
            );
          } else {
            openMessage(error?.data?.message, "error");
          }
        }
      })
      .finally(() => {
        handleFetchPortfolioData();
      });
  }, [state, exchangeToClientName, handleFetchPortfolioData]);

  const tryAgainLoading = checkAccountStatusFetching || createApiKeysLoading;
  const showNoFuturesAccountMessage =
    primaryExchange === PRIMARY_EXCHANGES.BINANCE &&
    hasAccessToken &&
    hasMatchAccount === false &&
    !createApiKeysFailed;

  const onNoFuturesTryAgain = useCallback(() => {
    if (binanceSelectedSubExchangesValid()) {
      createApiKeysHandler();
    } else {
      state && checkAccountStatus({ state });
    }
  }, [binanceSelectedSubExchangesValid, createApiKeysHandler, state]);

  const showQuickConnectAlreadyUsedWarning =
    createApiKeysFailed === QUICK_CONNECT_API_EXISTS_ERROR_CODE &&
    !!primaryExchange &&
    (
      [
        PRIMARY_EXCHANGES.BINANCE,
        PRIMARY_EXCHANGES.OKX,
        PRIMARY_EXCHANGES.BYBIT,
        PRIMARY_EXCHANGES.KUCOIN,
      ] as string[]
    ).includes(primaryExchange);

  const noFuturesAccountMessageSharedData = useMemo(
    () => ({
      show: showNoFuturesAccountMessage,
      errorMessage: `You don’t have a futures trading account.`,
      onSubmit: onNoFuturesTryAgain,
      loading: tryAgainLoading,
    }),
    [showNoFuturesAccountMessage, tryAgainLoading, onNoFuturesTryAgain]
  );

  const quickConnectAlreadyUsedMessageSharedData = useMemo(
    () => ({
      show: showQuickConnectAlreadyUsedWarning,
      errorMessage:
        createApiKeysErrorMessage ??
        "Only one set of API keys can be created per account using the quick connect.",
      onSubmit: createApiKeysHandler,
      loading: createApiKeysLoading,
      apiManagementLink: EXCHANGE_TO_API_MANAGEMENT_LINK[primaryExchange || ""],
    }),
    [
      showQuickConnectAlreadyUsedWarning,
      createApiKeysErrorMessage,
      createApiKeysHandler,
      createApiKeysLoading,
      primaryExchange,
    ]
  );

  const onBackToSelectExchange = () => {
    resetFormValues();
    resetQuickConnectState();
  };

  return {
    noFuturesAccountMessageSharedData,
    quickConnectAlreadyUsedMessageSharedData,
    primaryExchange,
    closeDrawer,
    tryAgainLoading,
    isLoading,
    quickConnectLoading,
    onBackToSelectExchange,
    isInitialized,
    quickConnectSucceeded,
    code,
    state,
  };
};

export default useQuickConnectProvider;

const EXCHANGE_TO_API_MANAGEMENT_LINK = {
  [PRIMARY_EXCHANGES.BINANCE]: BINANCE_API_MANAGEMENT_LINK,
  [PRIMARY_EXCHANGES.OKX]: OKX_API_MANAGEMENT_LINK,
  [PRIMARY_EXCHANGES.BYBIT]: BYBIT_API_MANAGEMENT_LINK,
  [PRIMARY_EXCHANGES.KUCOIN]: KUCOIN_API_MANAGEMENT_LINK,
};
